import { useEffect, useState } from 'react';
import countriesList from 'countries-list';
import { getTodayUnix } from '@general/dates';
import { regionList } from '@general/helpers/regions.helpers';
import useViewer from '@hooks/useViewer';

const generateFiltersFromSavedSearch = (search, additionalFilters = []) => {
    const filtersArray = [...additionalFilters, generateLocationFilter(search)];
    return filtersArray.filter(item => !!item.length).join(' AND ');
};

const generateOptionalFiltersFromSavedSearch = search => {
    const optionalFilters = generateSpaceFilters(search, 'tenants', 'space');
    return optionalFilters.filter(item => !!item.length);
};

const generateLocationFilter = search => {
    const locationsString = (search.businessLocations || [])
        .map(location => {
            if (location === 'CANADA') {
                return 'country:Canada';
            }

            if (location === 'INTERNATIONAL') {
                const countries = Object.keys(countriesList.countries).filter(
                    country => !['US', 'CA'].includes(country)
                );
                return countries.map(country => `country:"${country}"`).join(' OR ');
            }

            return regionList[location].subregions.map(subregion => `stateProvince:"${subregion.abbr}"`).join(' OR ');
        })
        .join(' OR ');

    if (locationsString.length > 0) {
        return `(${locationsString})`;
    }

    return '';
};

const generateSpaceFilters = search => [
    arrayOrFilterStrings('purpose', search.space.purpose),
    arrayOrFilterStrings('density', search.space.density),
    arrayOrFilterStrings('size', search.space.size),
    arrayOrFilterStrings('typeOfCenter', search.space.typeOfCenter),
    arrayOrFilterStrings('savedSearch', search.space.typeOfFacility),
];

const arrayOrFilterStrings = (filterKeyName, filterArray) =>
    filterArray.map(item => `${filterKeyName}:${item.toLowerCase().replace('_', '')}`).join(' OR ');

const usePropertyMatchFilters = ({ savedSearches, additionalFilters = '', excludeOptionalFilters = false }) => {
    const { viewer } = useViewer();
    const [filters, setFilters] = useState('objectID:PLACEHOLDER_FILTER');
    const [optionalFilters, setOptionalFilters] = useState([]);

    useEffect(() => {
        if (!viewer || !savedSearches || savedSearches.length === 0) {
            return;
        }

        let filterString = `expiresAtTimestamp >= ${getTodayUnix()} AND manager.status:Active`;

        const filteredSearches = savedSearches.filter(savedSearch => savedSearch.primaryInterest === 'SPACE');
        if (filteredSearches.length > 0) {
            filterString += ` AND (${filteredSearches
                .map(savedSearch => generateFiltersFromSavedSearch(savedSearch))
                .join(' OR ')})`;
        }

        if (filterString.length > 0 && additionalFilters.length > 0) {
            filterString += ' AND ';
        }

        if (additionalFilters.length > 0) {
            filterString += additionalFilters;
        }

        if (filterString.length > 0) {
            filterString += ` AND NOT manager.recordId:"${viewer.recordId}"`;
        }

        setFilters(filterString);

        if (excludeOptionalFilters) {
            setOptionalFilters(
                filteredSearches.reduce(
                    (allOptionalFilters, savedSearch) =>
                        allOptionalFilters.concat(generateOptionalFiltersFromSavedSearch(savedSearch)),
                    []
                )
            );
        }
    }, [viewer, additionalFilters, excludeOptionalFilters, savedSearches]);

    return { filters, optionalFilters };
};

export default usePropertyMatchFilters;
